import { useEffect, useState } from "react";
import { AccionesUsuarioCabecera, FilaVerComoMovil } from "@/components/admin/AccionesUsuarioCabecera";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Inbox, Workflow, BarChart3, Users, Landmark, ScrollText, Menu, PhoneCall, LifeBuoy,
  LucideIcon,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";
import {
  BankImpersonationProvider, useCurrentBanco, useRolNombreVista, useBancoScope,
} from "@/contexts/BankImpersonationContext";
import { useVistasDelRol } from "@/hooks/usePortalBancos/useVistasDelRol";
import { rutaParaMenu } from "@/lib/portal-bancos/broker";
import { usePerfilVista } from "@/hooks/usePerfilVista";
import { BankImpersonationSelector } from "./BankImpersonationSelector";
import { PortalTrackingProvider } from "@/contexts/PortalTrackingContext";
import { usePortalNavEstado } from "@/hooks/usePortalNav";
import { bancoThemeVars } from "@/lib/portal-bancos/branding";

const BANCOS_MENU_ID = 32;

const iconMap: Record<string, LucideIcon> = {
  "/admin/portal-bancos/solicitudes":  Inbox,
  "/admin/portal-bancos/proceso": Workflow,
  "/admin/portal-bancos/indicadores": BarChart3,
  "/admin/portal-bancos/ejecutivos":   Users,
  "/admin/portal-bancos/convenios":   Landmark,
  "/admin/portal-bancos/notarias": ScrollText,
  "/admin/portal-bancos/clientes":   PhoneCall,
  "/admin/portal-bancos/soporte":  LifeBuoy,
};

/**
 * Shell del portal. Vive DENTRO de `BankImpersonationProvider` porque necesita
 * el banco en scope (`useCurrentBanco`) para el branding: logo junto al nombre
 * del usuario y colores de marca aplicados a todo el portal.
 */
const PortalBancosShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const banco = useCurrentBanco();
  const rolVista = useRolNombreVista();
  // Colores del banco → variables CSS del design system. Sin color de marca (o
  // en la vista global "Super Administrador") se conserva el verde SOZU.
  const themeVars = bancoThemeVars(banco?.color_marca);

  const { items: navAll, cargando: navCargando } = usePortalNavEstado(BANCOS_MENU_ID, iconMap, Inbox);
  const { canReturnToAdmin } = useCanReturnToAdmin();
  const { isPathAllowed, isLoading: permisosCargando } = useAllowedMenus();

  // Navegación 100% desde BD: `submenus` del menú (usePortalNav) filtrado por el
  // permiso de lectura del rol. `isPathAllowed` ya descarta las vistas apagadas
  // (submenu/menú activo=false). Viendo como una persona, el menú es el de SU rol
  // (el rol con el que entra al portal), no el del Super Administrador.
  const { persona } = useBancoScope();
  const { data: vistasImpersonadas, isPending: vistasPendientes } = useVistasDelRol(persona?.rolAccesoId ?? null);
  const NAV = navAll.filter((i) =>
    persona
      ? // Mientras cargan los permisos del rol impersonado no se pinta de más.
        (vistasImpersonadas?.has(i.path) ?? false)
      : isPathAllowed(i.path),
  );

  // La ruta actual tiene que ser del menú. Si no (viendo como un banco y se
  // está en "Bancos", que administra los convenios de todos), se manda a la
  // primera vista del menú: esconderla pero dejarla abierta por URL sería el
  // mismo engaño. Sin menú cargado no se decide nada.
  useEffect(() => {
    if (NAV.length === 0) return;
    const ruta = location.pathname;
    if (!ruta.startsWith("/admin/portal-bancos/") || ruta.startsWith("/admin/portal-bancos/access-denied")) return;
    const permitida = NAV.some((i) => ruta === i.path || ruta.startsWith(i.path + "/"));
    if (!permitida) navigate(NAV[0].path, { replace: true });
    // NAV se recalcula en cada render; basta con sus rutas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [NAV.map((i) => i.path).join("|"), location.pathname, navigate]);

  // Lo que se marca en el menú (y el título). La vista del broker cuelga de su
  // sección, pero abierta desde Solicitudes o Proceso marca la de origen
  // (`?desde=`), si está en el menú. El permiso de arriba sigue con la ruta real.
  const rutaMenu = rutaParaMenu(location.pathname, location.search);
  const coincide = (ruta: string, p: string) => ruta === p || ruta.startsWith(p + "/");
  const rutaMarcada = NAV.some((i) => coincide(rutaMenu, i.path)) ? rutaMenu : location.pathname;
  const isActive = (p: string) => coincide(rutaMarcada, p);
  const current = NAV.find((i) => isActive(i.path))?.label ?? "Portal Bancos";

  // Perfil: con "Ver como", la persona impersonada (nombre y rol suyos), no el admin.
  const perfil = usePerfilVista({ rol: rolVista ?? "Bancos" });
  const userName = perfil.nombre.split(/\s+/).slice(0, 2).join(" ");
  const userRole = perfil.rol || "Bancos";
  const initials = perfil.iniciales;

  const go = (path: string) => { navigate(path); setMobileOpen(false); };

  const sidebar = (
    <PortalSidebar
      portal="Portal Bancos"
      miCuenta
      cargando={navCargando || permisosCargando || (persona?.rolAccesoId != null && vistasPendientes)}
      groups={[{
        items: NAV.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item.path),
          onSelect: () => go(item.path),
        })),
      }]}
      usuario={{
        nombre: userName,
        rol: userRole,
        iniciales: initials,
        avatar: banco ? <BancoMark banco={banco} className="size-8" redondo /> : undefined,
      }}
      onRegresar={canReturnToAdmin ? () => go("/admin") : undefined}
      onCerrarSesion={() => signOut()}
    />
  );

  return (
        <div className="min-h-screen flex antialiased" style={themeVars}>
          {/* Desktop sidebar */}
          <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
            {sidebar}
          </aside>

          {/* Mobile drawer */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
              {sidebar}
            </SheetContent>
          </Sheet>

          <div className="flex-1 lg:pl-64 min-w-0">
            {/* Desktop header */}
            <header className="hidden lg:flex items-center justify-between sticky top-0 z-20 bg-card border-b border-border-soft px-6 h-14">
              <div className="flex min-w-0 items-center gap-2.5">
                {banco && <BancoMark banco={banco} className="h-8 w-8 shrink-0" />}
                <h1 className="truncate text-xl font-bold tracking-tight text-foreground">{current}</h1>
              </div>
              <AccionesUsuarioCabecera verComo={<BankImpersonationSelector />} />
            </header>

            {/* Mobile header */}
            <header className="flex lg:hidden flex-col sticky top-0 z-20 bg-card border-b border-border">
              <div className="flex h-14 items-center justify-between px-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <button
                    onClick={() => setMobileOpen(true)}
                    className="p-2 -ml-1 rounded-md text-foreground hover:bg-muted transition-colors"
                    aria-label="Abrir menú"
                  >
                    <Menu className="h-5 w-5" />
                  </button>
                  <div className="flex min-w-0 items-center gap-2">
                    {banco && <BancoMark banco={banco} className="h-6 w-6 shrink-0" />}
                    <p className="truncate text-[15px] font-semibold tracking-tight text-foreground">{current}</p>
                  </div>
                </div>
                <AccionesUsuarioCabecera compacto />
              </div>
              <FilaVerComoMovil>
                <BankImpersonationSelector />
              </FilaVerComoMovil>
            </header>
            <main className="min-w-0 overflow-x-clip px-4 py-5 sm:px-6 lg:px-8 bg-background min-h-[calc(100vh-56px)]">
              <Outlet />
            </main>
          </div>
        </div>
  );
};

/**
 * Marca del banco: ícono cuadrado (`icono_url`) o, en su defecto, el logo
 * (`logo_url`). Sin imagen cargada cae a un cuadro con el color de marca y las
 * iniciales, para que el portal nunca quede sin identidad visual.
 */
function BancoMark({
  banco,
  className,
  redondo = false,
}: {
  banco: { nombre: string; color_marca: string | null; logo_url: string | null; icono_url: string | null };
  className?: string;
  /** Círculo, para cuando va junto al avatar del usuario y deben verse iguales. */
  redondo?: boolean;
}) {
  const forma = redondo ? "rounded-full" : "rounded-md";
  const src = banco.icono_url ?? banco.logo_url;
  if (src) {
    return (
      <img
        src={src}
        alt={banco.nombre}
        className={cn("shrink-0 border bg-white object-contain", forma, redondo && "p-1", className)}
      />
    );
  }
  const ini = banco.nombre.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("") || "B";
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center border text-[10px] font-bold text-white",
        forma,
        className,
      )}
      style={{ backgroundColor: banco.color_marca ?? "hsl(var(--muted-foreground))" }}
      role="img"
      aria-label={banco.nombre}
    >
      {ini}
    </span>
  );
}

export const PortalBancosLayout = () => (
  <BankImpersonationProvider>
    <PortalTrackingProvider portal="bancos">
      <PortalBancosShell />
    </PortalTrackingProvider>
  </BankImpersonationProvider>
);

export default PortalBancosLayout;
